import { useEffect, useState } from 'react';
import { ApiFout, leesWoorden, maakWoord, verwijderWoord, type Woord } from '../lib/api';
import type { Teksten } from '../teksten';

/**
 * De vangnet-woordenlijst.
 *
 * WAAROM DIT NAAST DE SPELLINGCORRECTIE BESTAAT. Die corrigeert wat aantoonbaar
 * fout gespeld is. Maar "Tillmann" IS goed gespeld - het is alleen niet wat er
 * gezegd werd. Het model heeft geen enkele reden om te twijfelen. Hier leg je
 * vast wat er bedoeld werd, en dan gaat het voortaan goed.
 *
 * INGEKLAPT BIJ HET OPENEN. Je hebt de lijst een paar keer per gesprek nodig,
 * niet altijd, en elke regel op het scherm duwt het gesprek naar beneden.
 */
export default function Woordenlijst({
  kamerId,
  t,
  voorstel,
  starttOpen = false,
}: {
  kamerId: string;
  t: Teksten;
  /** Vult het eerste veld vast in, bijvoorbeeld vanuit een verkeerd verstane zin. */
  voorstel?: string;
  /** true: begint uitgeklapt. Voor het aanbod bij een duim omlaag op het transcript. */
  starttOpen?: boolean;
}) {
  const [open, setOpen] = useState(starttOpen);
  const [woorden, setWoorden] = useState<Woord[]>([]);
  const [van, setVan] = useState(voorstel ?? '');
  const [naar, setNaar] = useState('');
  const [globaal, setGlobaal] = useState(true);
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    if (!open) return;
    let gestopt = false;
    void leesWoorden(kamerId)
      .then((lijst) => {
        if (!gestopt) setWoorden(lijst);
      })
      .catch(() => {
        if (!gestopt) setFout(t.lijstNietGeladen);
      });
    return () => {
      gestopt = true;
    };
  }, [open, kamerId, t]);

  async function toevoegen(): Promise<void> {
    setBezig(true);
    setFout(null);
    try {
      const woord = await maakWoord(kamerId, { van, naar, globaal });
      setWoorden((oud) => [woord, ...oud]);
      setVan('');
      setNaar('');
    } catch (error) {
      setFout(error instanceof ApiFout ? error.message : t.toevoegenMislukt);
    } finally {
      setBezig(false);
    }
  }

  async function weghalen(id: number): Promise<void> {
    // Meteen uit de lijst halen; komt de server met een fout, dan zetten we
    // hem terug. Wachten op het antwoord voelt traag voor iets dat vrijwel
    // altijd lukt.
    const bewaard = woorden;
    setWoorden((oud) => oud.filter((w) => w.id !== id));
    try {
      await verwijderWoord(kamerId, id);
    } catch {
      setWoorden(bewaard);
      setFout(t.verwijderenMislukt);
    }
  }

  const kanToevoegen = van.trim() !== '' && naar.trim() !== '' && !bezig;

  return (
    // Ingeklapt hoort klein en dun te zijn. Zie de toelichting bij de
    // instellingen in RoomPage: dit is de stand van rust en die mag geen
    // gespreksruimte kosten.
    <div className="mt-1.5 border-t border-slate-150 pt-1.5">
      <button
        type="button"
        onClick={() => setOpen((n) => !n)}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wide
                   text-slate-450 hover:text-slate-650 transition-colors duration-155"
      >
        <span>{t.woordenlijst}</span>
        {woorden.length > 0 && (
          <span className="font-mono text-slate-350">{woorden.length}</span>
        )}
        <span className="font-mono">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className="mt-2 animate-fade-in">
          <p className="text-[11px] text-slate-450 leading-relaxed mb-2">
            {t.woordenlijstUitleg}
          </p>

          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={van}
              onChange={(e) => setVan(e.target.value)}
              placeholder={t.hijHoort}
              className="flex-1 min-w-0 p-2 bg-slate-50 border-2 border-slate-250 rounded-xl
                         text-sm text-slate-800 placeholder-slate-405 focus:outline-none
                         focus:border-gio-blue focus:bg-white transition-colors duration-155"
            />
            <span className="self-center text-slate-350 shrink-0">→</span>
            <input
              type="text"
              value={naar}
              onChange={(e) => setNaar(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && kanToevoegen) void toevoegen();
              }}
              placeholder={t.jijBedoelt}
              className="flex-1 min-w-0 p-2 bg-slate-50 border-2 border-slate-250 rounded-xl
                         text-sm text-slate-800 placeholder-slate-405 focus:outline-none
                         focus:border-gio-blue focus:bg-white transition-colors duration-155"
            />
          </div>

          <label className="flex items-center gap-2 mb-2 text-[11px] text-slate-550 cursor-pointer">
            <input
              type="checkbox"
              checked={globaal}
              onChange={(e) => setGlobaal(e.target.checked)}
              className="w-3.5 h-3.5 accent-gio-orange cursor-pointer"
            />
            {t.ookVolgendeGesprekken}
          </label>

          {fout !== null && (
            <p className="text-[11px] text-gio-danger mb-2">{fout}</p>
          )}

          <button
            type="button"
            onClick={() => void toevoegen()}
            disabled={!kanToevoegen}
            className="w-full py-2 rounded-xl bg-gio-blue hover:bg-gio-dark text-white
                       font-bold text-xs transition-colors duration-155 disabled:opacity-40
                       disabled:cursor-not-allowed"
          >
            {bezig ? t.bezig : t.voegToe}
          </button>

          {woorden.length > 0 && (
            <ul className="mt-3 space-y-1 max-h-40 overflow-y-auto pr-1">
              {woorden.map((w) => (
                <li
                  key={w.id}
                  className="flex items-center justify-between gap-2 bg-slate-50 border-2
                             border-slate-150 rounded-xl px-2.5 py-1.5"
                >
                  <span className="font-mono text-[11px] text-slate-650 min-w-0 truncate">
                    <span className="text-gio-danger">{w.van}</span>
                    <span className="text-slate-350 mx-1.5">→</span>
                    <span className="text-emerald-650 font-bold">{w.naar}</span>
                    {w.globaal && <span className="text-slate-350 ml-1.5">{t.overal}</span>}
                  </span>
                  <button
                    type="button"
                    onClick={() => void weghalen(w.id)}
                    aria-label={`${t.verwijder} ${w.van}`}
                    className="shrink-0 w-6 h-6 rounded-lg text-slate-400 hover:text-gio-danger
                               hover:bg-gio-danger/10 transition-colors duration-155
                               flex items-center justify-center"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
